import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Check, X, Trash2, RefreshCw, ExternalLink, Filter } from 'lucide-react';
import { toast } from 'sonner';
import { getBackendUrl } from '../utils/api';

const STATUS_FILTERS = [
  { key: 'pending', label: 'Εκκρεμή', color: 'amber' },
  { key: 'approved', label: 'Εγκεκριμένα', color: 'emerald' },
  { key: 'rejected', label: 'Απορριφθέντα', color: 'red' },
  { key: 'all', label: 'Όλα', color: 'slate' },
];

const StreamUrlSubmissionsPanel = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const apiBase = getBackendUrl();
  const token = localStorage.getItem('token');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter === 'all' ? '' : `?status=${statusFilter}`;
      const resp = await fetch(`${apiBase}/api/stream-url-submissions/list${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setItems(data.submissions || []);
      } else {
        toast.error('Σφάλμα φόρτωσης');
      }
    } catch {
      toast.error('Σφάλμα δικτύου');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, apiBase, token]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleAction = async (id, action) => {
    const confirmMsg = action === 'approve'
      ? 'Εγκρίνετε αυτήν την υποβολή; Το stream_url του σταθμού θα αντικατασταθεί.'
      : action === 'reject' ? 'Απορρίπτετε την υποβολή;' : 'Διαγραφή εγγραφής;';
    if (!window.confirm(confirmMsg)) return;

    const method = action === 'delete' ? 'DELETE' : 'POST';
    const path = action === 'delete' ? '' : `/${action}`;
    const resp = await fetch(`${apiBase}/api/stream-url-submissions/${id}${path}`, {
      method,
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (resp.ok) {
      toast.success(
        action === 'approve' ? 'Εγκρίθηκε · το URL εφαρμόστηκε στον σταθμό'
          : action === 'reject' ? 'Απορρίφθηκε'
          : 'Διαγράφηκε'
      );
      fetchList();
    } else {
      const data = await resp.json().catch(() => ({}));
      toast.error(data.detail || 'Σφάλμα');
    }
  };

  const statusBadge = (s) => {
    const map = {
      pending: { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-800 dark:text-amber-300', label: 'Εκκρεμεί' },
      approved: { bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-800 dark:text-emerald-300', label: 'Εγκεκριμένο' },
      rejected: { bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-800 dark:text-red-300', label: 'Απορριφθέν' },
    };
    const m = map[s] || map.pending;
    return <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${m.bg} ${m.text}`}>{m.label}</span>;
  };

  return (
    <div data-testid="stream-url-submissions-panel" className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <ExternalLink className="w-5 h-5 text-indigo-500" />
            Stream URL Submissions
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Προτάσεις από χρήστες για αντικατάσταση stream URLs
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="flex gap-1 bg-gray-100 dark:bg-gray-900 rounded-md p-1">
            {STATUS_FILTERS.map(f => (
              <button
                key={f.key}
                data-testid={`stream-url-filter-${f.key}`}
                onClick={() => setStatusFilter(f.key)}
                className={`px-2.5 py-1 text-xs rounded transition-colors ${
                  statusFilter === f.key
                    ? 'bg-indigo-600 text-white font-semibold'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button
            data-testid="stream-url-refresh"
            onClick={fetchList}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            title="Ανανέωση"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-gray-400 dark:text-gray-500">
          <Filter className="w-8 h-8 mx-auto mb-2 opacity-40" />
          Δεν υπάρχουν υποβολές {statusFilter !== 'all' && `με κατάσταση "${STATUS_FILTERS.find(f => f.key === statusFilter)?.label}"`}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 px-2 font-medium">Ημ/νία</th>
                <th className="py-2 px-2 font-medium">Χρήστης</th>
                <th className="py-2 px-2 font-medium">Σταθμός</th>
                <th className="py-2 px-2 font-medium">URL</th>
                <th className="py-2 px-2 font-medium">Σημείωση</th>
                <th className="py-2 px-2 font-medium">Status</th>
                <th className="py-2 px-2 font-medium text-right">Ενέργειες</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {items.map(it => (
                <tr key={it.id} data-testid={`submission-row-${it.id}`} className="hover:bg-gray-50 dark:hover:bg-gray-900/40">
                  <td className="py-2 px-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    {it.submitted_at ? new Date(it.submitted_at).toLocaleString('el-GR') : '—'}
                  </td>
                  <td className="py-2 px-2">
                    <div className="font-medium text-gray-800 dark:text-gray-200">{it.user_username}</div>
                    <div className="text-[11px] text-gray-500">{it.user_email}</div>
                  </td>
                  <td className="py-2 px-2 font-medium text-gray-800 dark:text-gray-200">{it.station_name}</td>
                  <td className="py-2 px-2 max-w-[280px]">
                    <a href={it.url} target="_blank" rel="noopener noreferrer"
                       className="text-indigo-600 dark:text-indigo-400 hover:underline break-all text-xs">
                      {it.url}
                    </a>
                  </td>
                  <td className="py-2 px-2 text-xs text-gray-600 dark:text-gray-400 max-w-[200px] truncate" title={it.note}>
                    {it.note || '—'}
                  </td>
                  <td className="py-2 px-2">{statusBadge(it.status)}</td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-1 justify-end">
                      {it.status === 'pending' && (
                        <>
                          <button
                            data-testid={`approve-${it.id}`}
                            onClick={() => handleAction(it.id, 'approve')}
                            className="p-1.5 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400"
                            title="Εφαρμογή στο station"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`reject-${it.id}`}
                            onClick={() => handleAction(it.id, 'reject')}
                            className="p-1.5 rounded bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400"
                            title="Απόρριψη"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      <button
                        data-testid={`delete-${it.id}`}
                        onClick={() => handleAction(it.id, 'delete')}
                        className="p-1.5 rounded bg-gray-500/10 hover:bg-gray-500/20 text-gray-600 dark:text-gray-400"
                        title="Διαγραφή"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StreamUrlSubmissionsPanel;
